import { useState, useEffect, FC, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Popover, MenuItem, CircularProgress, Typography, Box } from '@mui/material';
import { PinDrop } from '@mui/icons-material';

import { useGoogleMapsApiLoader } from '@frontend/features/common/hooks/useGoogleMapsApi';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { useNimbuxHotelsList } from '@frontend/features/hotel-content/hooks/useNimbuxHotelsList';
import { CityDescription, HotelPropertySearchDTO, LocationSelection, SearchData } from './types';
import { toProperName } from '@frontend/features/common/helper';

interface HotelSearchInputProps {
  setSearchLocation: (value: LocationSelection) => void;
  setSearchProperty: (value: HotelPropertySearchDTO) => void;
  setSearchType: (value: 'location' | 'property') => void;
  searchData: SearchData;
}

export const HotelSearchInput: FC<HotelSearchInputProps> = ({ setSearchLocation, setSearchProperty, setSearchType, searchData }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [debouncedAddress, setDebouncedAddress] = useDebouncedState('', 300);
  const [suggestions, setSuggestions] = useState<CityDescription[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const placesService = useRef<google.maps.places.AutocompleteService>();
  const { isLoaded: placesLoaded } = useGoogleMapsApiLoader();

  // Buscar hoteles usando el hook
  const { allHotels, isLoading: isFetchingApi } = useNimbuxHotelsList({
    pageSize: 5,
    name: debouncedAddress.length > 2 ? debouncedAddress : undefined,
  });

  // Mapear los hoteles de Nimbux al formato esperado
  const hotelSuggestions: HotelPropertySearchDTO[] = useMemo(() => {
    return allHotels.map((hotel) => ({
      hotelName: hotel.name || '',
      nimbuxHotelId: hotel.id?.toString() || '',
    }));
  }, [allHotels]);

  const handleSelect = (value: CityDescription) => {
    if (value?.placeId) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: value.placeId }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setSearchType('location');
          setAddress(results[0].formatted_address);
          setSearchLocation({
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng(),
            description: results[0].formatted_address,
            placeId: value.placeId,
          });
        }
      });
    }
    setAnchorEl(null);
  };

  const handleSelectNimbuxProperty = (option: HotelPropertySearchDTO) => {
    setAddress(option.hotelName);
    setSearchType('property');
    setSearchProperty(option);
    setAnchorEl(null);
  };

  const handleSearchLocation = () => {
    if (!placesLoaded || address.length < 3) return;

    placesService.current?.getPlacePredictions({ input: address }, (predictions) => {
      if (predictions) {
        setSuggestions(
          predictions.map((p) => ({
            description: p.description,
            placeId: p.place_id,
          }))
        );
      }
    });
  };

  useEffect(() => {
    if (placesLoaded && !placesService.current) {
      placesService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [placesLoaded]);

  useEffect(() => {
    if (searchData.property?.hotelName) {
      setAddress(searchData.property.hotelName);
    } else if (searchData.location?.description) {
      setAddress(searchData.location.description);
    }
  }, [searchData.property, searchData.location]);

  useEffect(() => {
    handleSearchLocation();
    setDebouncedAddress(address);
  }, [address]);

  return (
    <Box width="100%" position="relative">
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={t('hotels.searcher.destination')}
        onFocus={(e) => {
          setIsFocused(true);
          setAnchorEl(e.currentTarget);
        }}
        onBlur={() => {
          setIsFocused(false);
          setAnchorEl(null);
        }}
        InputProps={{
          startAdornment: <PinDrop />,
          endAdornment: isFetchingApi ? <CircularProgress size={20} /> : null,
        }}
      />
      <Popover
        anchorEl={anchorEl}
        open={isFocused && address.length > 0 && (suggestions.length > 0 || Boolean(hotelSuggestions && hotelSuggestions.length > 0))}
        onClose={() => {
          setIsFocused(false);
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ mt: 1, ml: -5 }}
      >
        <Box sx={{ minWidth: 300, width: Number(anchorEl?.clientWidth! + 24), maxHeight: 300, overflowY: 'auto', p: 1 }}>
          {hotelSuggestions && hotelSuggestions.length > 0 && (
            <>
              <Typography variant="caption" sx={{ pl: 2, pt: 1, pb: 1 }}>{t('hotels.searcher.hotels')}</Typography>
              {hotelSuggestions.map((option) => (
                <MenuItem
                  key={option.nimbuxHotelId}
                  onClick={() => handleSelectNimbuxProperty(option)}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis' //FIXME: este elipsis no esta andando
                  }}>
                  {toProperName(option.hotelName)}
                </MenuItem>
              ))}
            </>
          )}

          {suggestions.length > 0 && (
            <>
              <Typography variant="caption" sx={{ pl: 2, pt: 1, pb: 1 }}>{t('hotels.searcher.locations')}</Typography>
              {suggestions.map((option) => (
                <MenuItem
                  key={option.placeId}
                  onClick={() => handleSelect(option)}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis' //FIXME: este elipsis no esta andando
                  }}>
                  {option.description}
                </MenuItem>
              ))}
            </>
          )}

          {(suggestions.length === 0 && hotelSuggestions?.length === 0) && (
            <Typography variant="body2" sx={{ pl: 2, pt: 1, pb: 1 }}>
              {t('hotels.searcher.noResults')}
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
};
